import {
  CellError,
  CellStatuses,
  ErrorType,
  FunctionArgumentType,
  FunctionPlugin,
  HyperFormula,
  ImplementedFunctions,
  Sheets,
  RowIdentifier,
  IdeasFile,
  SimpleRangeValue,
  EmptyValue,
  RoiFrame,
  Group,
  IdeasFileNotFound,
} from "@inscopix/ideas-hyperformula";
import { chain, zip } from "lodash";
import { immer } from "zustand/middleware/immer";
import { DataTableStoreState } from "./DataTableProvider.types";
import assert from "assert";
import { isDefined } from "utils/isDefined";
import { ProcedureAst } from "@inscopix/ideas-hyperformula/typings/parser";
import { InterpreterState } from "@inscopix/ideas-hyperformula/typings/interpreter/InterpreterState";
import {
  InternalScalarValue,
  InterpreterValue,
  RawInterpreterValue,
  RawNoErrorScalarValue,
} from "@inscopix/ideas-hyperformula/typings/interpreter/InterpreterValue";
import { isToolRoiFrameParamValue } from "types/ToolRoiFrameParamValue/isToolRoiFrameParamValue";
import { CellStatus } from "components/CellStatusEditor/CellStatusEditor.types";
import { JsonValue } from "type-fest";

/** Represents the `get` function used to read a Zustand store */
type GetFn = Parameters<
  Parameters<typeof immer<DataTableStoreState, [], []>>[0]
>[1];

/**
 * Represents a cell value that is still loading.
 *
 * Since HyperFormula does not currently support async formulas, we use
 * a cell error to propagate the loading state to all dependent cells.
 */
export class CellErrorLoading extends CellError {
  static message = "Value is loading." as const;
  constructor() {
    super(ErrorType.NA, CellErrorLoading.message);
  }
}

/** Represents the context passed to all IDEAS formulas. */
type IdeasFunctionPluginContext = {
  engine: HyperFormula;
  get: GetFn;
};

/**
 * Compares two cell values and determines if they represent that same data.
 * @param a
 * @param b
 * @returns `true` if the cell values are equal, `false` otherwise.
 */
export const areCellValuesEqual = (
  a: InternalScalarValue,
  b: InternalScalarValue,
): boolean => {
  if (
    typeof a === "boolean" ||
    typeof a === "number" ||
    typeof a === "string" ||
    typeof a === "symbol" ||
    a === null
  ) {
    return a === b;
  } else if (a instanceof IdeasFile && b instanceof IdeasFile) {
    return a.attrs.id === b.attrs.id;
  } else if (a instanceof RowIdentifier && b instanceof RowIdentifier) {
    return a.tableKey === b.tableKey && a.rowIndex === b.rowIndex;
    // Check if the group contains the value
  } else if (b instanceof Group) {
    return b.cellValues.some((val) =>
      areCellValuesEqual(a, val as InternalScalarValue),
    );
  } else {
    return false;
  }
};

/** Custom formula plugin for IDEAS data types and operations */
export class IdeasFunctionPlugin extends FunctionPlugin {
  public static implementedFunctions: ImplementedFunctions = {
    CELL_STATUSES: {
      method: "cell_statuses",
      parameters: [
        // Status of each cell
        { argumentType: FunctionArgumentType.INTEGER },
      ],
      repeatLastArgs: 1,
    },

    FILE: {
      method: "file",
      parameters: [
        // File ID
        { argumentType: FunctionArgumentType.STRING },
      ],
    },

    GROUP: {
      method: "group",
      parameters: [
        // Value stored in the group
        { argumentType: FunctionArgumentType.ANY },
      ],
      repeatLastArgs: 1,
    },

    JOIN: {
      method: "join",
      parameters: [
        // Left cell value
        { argumentType: FunctionArgumentType.NOERROR },
        // Right column
        { argumentType: FunctionArgumentType.RANGE },
        // Source column
        { argumentType: FunctionArgumentType.RANGE },
      ],
    },

    METADATUM: {
      method: "metadatum",
      parameters: [
        // File
        { argumentType: FunctionArgumentType.NOERROR },
        // Metadatum key
        { argumentType: FunctionArgumentType.STRING },
        // Value in array
        { argumentType: FunctionArgumentType.NOERROR, optionalArg: true },
      ],
    },

    MULTI_JOIN: {
      method: "multi_join",
      parameters: [
        // Source column
        { argumentType: FunctionArgumentType.RANGE },
        // Left cell value
        { argumentType: FunctionArgumentType.NOERROR },
        // Right column
        { argumentType: FunctionArgumentType.RANGE },
      ],
      repeatLastArgs: 2,
    },

    ROW_IDENTIFIER: {
      method: "row_identifier",
      parameters: [
        // Table key
        { argumentType: FunctionArgumentType.STRING },
        // Row index
        { argumentType: FunctionArgumentType.NUMBER },
      ],
    },

    ROI_FRAME: {
      method: "roi_frame",
      parameters: [
        // Serialized shape definitions
        { argumentType: FunctionArgumentType.STRING },
      ],
    },

    ROW_INDEX: {
      method: "row_index",
      parameters: [],
    },

    TABLE_KEY: {
      method: "table_key",
      parameters: [],
    },
  };

  /**
   * Creates a new `CellStatuses` cell value.
   * @param ast
   * @param state
   * @returns The cell statuses.
   */
  cell_statuses(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("CELL_STATUSES"),
      (...values: number[]) => {
        // Check whether all arguments are valid cell statuses
        const invalidStatuses = chain(values)
          .filter((value) => !(value in CellStatus))
          .uniq()
          .sort()
          .value();

        if (invalidStatuses.length > 0) {
          const msg = `Invalid cell statuses: ${invalidStatuses.join(", ")}`;
          return new CellError(ErrorType.ERROR, msg);
        } else {
          return new CellStatuses(values);
        }
      },
    );
  }

  /**
   * Creates a new `IdeasFile` cell value.
   * @param ast
   * @param state
   * @returns A `CellErrorLoading` while the file data is being fetched and
   * an `IdeasFile` thereafter.
   */
  file(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("FILE"),
      (fileReferenceId: string) => {
        const context = this.config.context as IdeasFunctionPluginContext;
        const { get, engine } = context;
        const file = get().getFile({
          fileReferenceId,
          address: state.formulaAddress,
          engine,
        });

        if (file === undefined) {
          return new CellErrorLoading();
        } else if (file === IdeasFileNotFound) {
          return IdeasFileNotFound;
        } else {
          return new IdeasFile({
            id: file.id,
            referenceId: file.reference_id,
            name: file.name,
            status: file.status,
            fileType: file.file_type,
            tags: file.tags,
            isSeries: file.is_series,
            source: "uploaded",
            processingStatus: file.processing_status,
            seriesParentId: file.series_parent_id,
          });
        }
      },
    );
  }

  /**
   * Creates a new `Group` cell value.
   * @param ast
   * @param state
   * @returns The group.
   */
  group(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("GROUP"),
      (...values: RawInterpreterValue[]) => {
        const context = this.config.context as IdeasFunctionPluginContext;
        const { engine } = context;
        if (values.length === 0) {
          return EmptyValue;
        } else if (values.length === 1) {
          return values[0];
        } else {
          const exportedValues = values.map((val) => engine.exportValue(val));
          return new Group(exportedValues);
        }
      },
    );
  }

  /**
   * Creates a new `IdeasFile` cell value.
   * @param ast
   * @param state
   * @returns A `CellErrorLoading` while the file data is being fetched and
   * an `IdeasFile` thereafter.
   */
  join(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("JOIN"),
      (
        leftCellValue: RawNoErrorScalarValue,
        rightCol: SimpleRangeValue,
        sourceCol: SimpleRangeValue,
      ) => {
        const context = this.config.context as IdeasFunctionPluginContext;
        const { engine, get } = context;

        if (rightCol.height !== sourceCol.height) {
          return new CellError(
            ErrorType.ERROR,
            "Right column and source column are different heights",
          );
        }

        if (rightCol.width() !== 1 || sourceCol.width() !== 1) {
          return new CellError(
            ErrorType.ERROR,
            "Right column and source column cannot span multiple columns",
          );
        }

        // Cell values we do not allow joining on
        const nonJoinableValues: InternalScalarValue[] = ["", EmptyValue];

        const rawValues =
          // Pair the values from the right column and source column together
          zip(rightCol.data, sourceCol.data)
            .filter((_, idx) => {
              // Includes pairs with missing range data
              if (
                rightCol.range === undefined ||
                sourceCol.range === undefined
              ) {
                return true;
              }

              const rightTableKey = engine.getSheetName(rightCol.range.sheet);
              const rightTable = get().tables.find(
                (t) => t.key === rightTableKey,
              );
              const rightRowIndex = rightCol.range.start.row + idx;
              const rightRow = rightTable?.rows[rightRowIndex];
              const sourceTableKey = engine.getSheetName(sourceCol.range.sheet);
              const sourceTable = get().tables.find(
                (t) => t.key === sourceTableKey,
              );
              const sourceRowIndex = sourceCol.range.start.row + idx;
              const sourceRow = sourceTable?.rows[sourceRowIndex];

              // Filter out pairs where either the right or source row are not joinable
              if (!rightRow?.joinable || !sourceRow?.joinable) {
                return false;
              }

              return true;
            })
            // Remove any pairs where the right column cell value is not the
            // same as the left column cell value. We already ensured that the
            // right column spans exactly one column, hence [0]. If either value
            // is non-joinable, do not consider the values equal.
            .filter(([rightCellValues]) => {
              assert(isDefined(rightCellValues));
              const rightCellValue = rightCellValues[0];
              return (
                !nonJoinableValues.includes(leftCellValue) &&
                !nonJoinableValues.includes(rightCellValue) &&
                areCellValuesEqual(leftCellValue, rightCellValue)
              );
            })
            // Get the source column cell value from any pair with a match.
            // We already ensured that the source column spans exactly one
            // column, hence [0].
            .map(([_, sourceCellValues]) => {
              assert(isDefined(sourceCellValues));
              return sourceCellValues[0];
            });

        if (rawValues.length === 0) {
          return EmptyValue;
        } else if (rawValues.length === 1) {
          return rawValues[0];
        } else {
          const values = rawValues.map((val) => engine.exportValue(val));
          return new Group(values);
        }
      },
    );
  }

  /**
   * Reads metadatum from an `IdeasFile`.
   * @param ast
   * @param state
   * @returns A `CellErrorLoading` while the metadatum is being fetched and
   * the metadatum value thereafter.
   */
  metadatum(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("METADATUM"),
      (
        file: RawNoErrorScalarValue,
        metadatumKey: string,
        arrayValue?: RawNoErrorScalarValue,
      ) => {
        if (file === EmptyValue || file === "") {
          return EmptyValue;
        }

        const isGroupOfFiles =
          file instanceof Group &&
          file.cellValues.every((value) => value instanceof IdeasFile);

        if (!(file instanceof IdeasFile) && !isGroupOfFiles) {
          return new CellError(ErrorType.VALUE, "Wrong type of argument.");
        }

        const context = this.config.context as IdeasFunctionPluginContext;
        const { engine, get } = context;

        const fetchMetadatum = (file: IdeasFile) => {
          const metadatumValue = get().getFileMetadatum({
            fileId: file.attrs.id,
            metadatumKey,
            address: state.formulaAddress,
            engine,
          });

          if (metadatumValue instanceof Error) {
            return new CellError(ErrorType.ERROR, metadatumValue.message);
          } else {
            return metadatumValue;
          }
        };

        const formatValue = (value: JsonValue | undefined) => {
          if (
            typeof value === "boolean" ||
            typeof value === "string" ||
            typeof value === "number"
          ) {
            return value;
          } else if (value === null || value === undefined) {
            return null;
          } else {
            return JSON.stringify(value);
          }
        };

        if (file instanceof IdeasFile) {
          const value = fetchMetadatum(file);
          if (Array.isArray(value) && arrayValue !== undefined) {
            if (value.includes(arrayValue)) {
              return arrayValue;
            } else {
              return EmptyValue;
            }
          } else if (value instanceof CellError) {
            return value;
          } else {
            return formatValue(value) ?? EmptyValue;
          }
        } else {
          const files = file.cellValues as IdeasFile[];
          const metadatumValues = files.map(fetchMetadatum);

          const validValues = metadatumValues.filter(
            (value) => !(value instanceof CellError),
          ) as (JsonValue | undefined)[];

          if (validValues.length !== metadatumValues.length) {
            return new CellError(
              ErrorType.ERROR,
              "One or more files do not have the requested metadatum.",
            );
          } else {
            const values = validValues.flat();
            if (arrayValue !== undefined) {
              if (values.includes(arrayValue as JsonValue)) {
                return arrayValue;
              } else {
                return EmptyValue;
              }
            }
            return new Group(validValues.map(formatValue));
          }
        }
      },
    );
  }

  /**
   * Reads cells values from a source columns from every rows where all left and
   * right columns match.
   * @param ast
   * @param state
   * @returns The joined cell values.
   */
  multi_join(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("MULTI_JOIN"),
      (sourceCol: SimpleRangeValue, ...args) => {
        const context = this.config.context as IdeasFunctionPluginContext;
        const { engine, get } = context;

        const pairs: [
          leftCellValue: RawNoErrorScalarValue,
          rightCol: SimpleRangeValue,
        ][] = [];

        for (let i = 0; i < args.length; i += 2) {
          const leftCellValue = args[i] as RawNoErrorScalarValue;
          const rightCol = args[i + 1] as SimpleRangeValue;
          pairs.push([leftCellValue, rightCol]);
        }

        for (const [, rightCol] of pairs) {
          // Validate that right column has the same height as the source column
          if (rightCol.height() !== sourceCol.height()) {
            return new CellError(
              ErrorType.ERROR,
              "Right column and source column are different heights",
            );
          }

          // Validate that right column and source column span exactly one column
          if (rightCol.width() !== 1 || sourceCol.width() !== 1) {
            return new CellError(
              ErrorType.ERROR,
              "Right column and source column cannot span multiple columns",
            );
          }
        }

        // Cell values we do not allow joining on
        const nonJoinableValues: InternalScalarValue[] = ["", EmptyValue];

        // Store all source column cell values where
        const rawValues: InternalScalarValue[] = [];

        // Iterate through each row in the source column and check if the left
        // and right cell values match in each pair. We already ensured that the
        // source column spans exactly one column, hence [0].
        for (let rowIdx = 0; rowIdx < sourceCol.height(); rowIdx++) {
          const sourceCellValue = sourceCol.data[rowIdx][0];

          const matchesAll = pairs.every(([leftCellValue, rightCol]) => {
            const rightCellValue = rightCol.data[rowIdx][0];

            // Exclude pairs with missing range data
            if (rightCol.range === undefined || sourceCol.range === undefined) {
              return false;
            }

            const rightTableKey = engine.getSheetName(rightCol.range.sheet);
            const rightTable = get().tables.find(
              (t) => t.key === rightTableKey,
            );
            const rightRowIndex = rightCol.range.start.row + rowIdx;
            const rightRow = rightTable?.rows[rightRowIndex];
            const sourceTableKey = engine.getSheetName(sourceCol.range.sheet);
            const sourceTable = get().tables.find(
              (t) => t.key === sourceTableKey,
            );
            const sourceRowIndex = sourceCol.range.start.row + rowIdx;
            const sourceRow = sourceTable?.rows[sourceRowIndex];

            // Filter out pairs where either the right or source row are not joinable
            if (!rightRow?.joinable || !sourceRow?.joinable) {
              return false;
            }

            return (
              !nonJoinableValues.includes(leftCellValue) &&
              !nonJoinableValues.includes(rightCellValue) &&
              areCellValuesEqual(leftCellValue, rightCellValue)
            );
          });

          if (matchesAll) {
            rawValues.push(sourceCellValue);
          }
        }

        if (rawValues.length === 0) {
          return EmptyValue;
        } else if (rawValues.length === 1) {
          return rawValues[0];
        } else {
          const values = rawValues.map((val) => engine.exportValue(val));
          return new Group(values);
        }
      },
    );
  }

  /**
   * Creates a new `RoiFrame` cell value from serialized shape data.
   * @param ast
   * @param state
   * @returns The ROI frame.
   */
  roi_frame(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("ROI_FRAME"),
      (shapeData: string) => {
        try {
          // Serialized shape data contains escaped double quotes
          const unescapedShapeData = shapeData.replace(/\\"/g, '"');
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const shapes = JSON.parse(unescapedShapeData);
          assert(isToolRoiFrameParamValue(shapes));
          return new RoiFrame(shapes);
        } catch (error) {
          return new CellError(ErrorType.ERROR, "Invalid ROI frame");
        }
      },
    );
  }

  /**
   * Creates a new `RowIdentifier` cell value.
   * @param ast
   * @param state
   * @returns The row identifier.
   */
  row_identifier(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("ROW_IDENTIFIER"),
      (tableKey: string, rowIndex: number) => {
        const { get } = this.config.context as IdeasFunctionPluginContext;
        const table = get()?.tables.find((table) => table.key === tableKey);
        const row = table?.rows.find((row) => row.index === rowIndex);
        return isDefined(row)
          ? new RowIdentifier(tableKey, rowIndex)
          : new CellError(ErrorType.ERROR, "Row does not exist");
      },
    );
  }

  /**
   * Gets the index of the row of the formula reference.
   * @param ast
   * @param state
   * @returns The row index.
   */
  row_index(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(ast.args, state, this.metadata("ROW_INDEX"), () => {
      const { engine, get } = this.config.context as IdeasFunctionPluginContext;
      const sheetId = state.formulaAddress.sheet;
      const rowId = state.formulaAddress.row;
      const tableKey = engine.getSheetName(sheetId);
      const table = get()?.tables.find((table) => table.key === tableKey);
      const row = table?.rows[rowId];
      return row?.index ?? new CellError(ErrorType.ERROR, "Row does not exist");
    });
  }

  /**
   * Gets the key of the table of the formula reference.
   * @param ast
   * @param state
   * @returns The table key.
   */
  table_key(ast: ProcedureAst, state: InterpreterState): InterpreterValue {
    return this.runFunction(ast.args, state, this.metadata("TABLE_KEY"), () => {
      const context = this.config.context as IdeasFunctionPluginContext;
      const sheetId = state.formulaAddress.sheet;
      const tableKey = context.engine.getSheetName(sheetId);
      return tableKey ?? new CellError(ErrorType.ERROR, "Table does not exist");
    });
  }
}

// Register english translations for each formula name
export const IdeasFunctionPluginTranslations = {
  enGB: {
    CELL_STATUSES: "CELL_STATUSES",
    FILE: "FILE",
    GROUP: "GROUP",
    JOIN: "JOIN",
    METADATUM: "METADATUM",
    MULTI_JOIN: "MULTI_JOIN",
    ROW_IDENTIFIER: "ROW_IDENTIFIER",
    ROI_FRAME: "ROI_FRAME",
    ROW_INDEX: "ROW_INDEX",
    TABLE_KEY: "TABLE_KEY",
  },
};

// Register the IDEAS plugin with HyperFormula
HyperFormula.registerFunctionPlugin(
  IdeasFunctionPlugin,
  IdeasFunctionPluginTranslations,
);

/**
 * Creates a new instance of a HyperFormula spreadsheet engine.
 * @param sheets The initial cell formulas formatted as sheets.
 * @param get `get` function provided by the Zustand store.
 * @returns The spreadsheet engine.
 */
export const initSpreadsheetEngine = (sheets: Sheets, get: GetFn) => {
  // Initialize a new HyperFormula engine instance
  const engine = HyperFormula.buildFromSheets(sheets);
  engine.updateConfig({
    licenseKey: "gpl-v3",
    context: { engine, get } satisfies IdeasFunctionPluginContext,
    // Disable parsing text values as dates and times
    dateFormats: [],
    timeFormats: [],
  });
  return engine;
};
